import { differenceInSeconds } from 'date-fns';
import { update } from 'ramda';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { fetchLootboxModal, type LootboxApi } from '@/api/cms/lootbox';
import {
  type Lootbox as LootboxModalData,
  playerLootboxActivation,
} from '@/api/common';
import { ButtonClose } from '@/components/buttons';
import type { ScButtonCloseProps } from '@/components/buttons/styled';
import { ImageComponent } from '@/components/Image';
import { Modal } from '@/components/Modal';
import { TimerLabel } from '@/components/Timer';
import { getCDNImageUrl } from '@/helpers/cdn';
import { useGetCommonStore } from '@/store';
import { Skeleton } from '../ModalSkeleton';
import { Lootbox } from './components/LootboxItem';
import { ScLootboxModal } from './styled';

const defaultChar = '/lootbox/defaultCharacter.webp';

interface LootboxModalProps {
  lootboxId: number;
}

export const LootboxModal = forwardRef<HTMLDivElement, LootboxModalProps>(
  ({ lootboxId }, ref) => {
    const {
      i18n: { language },
    } = useTranslation('common');
    const useCommonStore = useGetCommonStore();

    const { isMobileDevice, withoutTagsLootboxes, fetchLootboxes } =
      useCommonStore(
        useShallow((state) => ({
          isMobileDevice: state.isMobileDevice,
          withoutTagsLootboxes: state.withoutTagsLootboxes,
          fetchLootboxes: state.fetchLootboxes,
        }))
      );

    const [lootboxModalData, setLootboxModalData] = useState<LootboxApi>();
    const [lootboxModalDataPending, setLootboxModalDataPending] =
      useState(false);

    const [currentLootbox, setCurrentLootbox] =
      useState<LootboxModalData | null>(null);
    const [selectedPrizeId, setSelectedPrizeId] = useState<number | null>(null);

    const [isStopedUpdating, setIsStopedUpdating] = useState(false);

    const timeLeft = useMemo(() => {
      if (!currentLootbox?.validUntil) return 0;
      const validUntilDate = new Date(currentLootbox.validUntil);
      const currentDate = new Date();
      return differenceInSeconds(validUntilDate, currentDate);
    }, [currentLootbox]);

    const buttonStyle: ScButtonCloseProps = useMemo(
      () => (isMobileDevice ? { top: 12, right: 12 } : { top: 16, right: 16 }),
      [isMobileDevice]
    );

    const onClose = () =>
      useCommonStore.setState({
        activeLootboxId: undefined,
        dismissedLootboxId: lootboxId,
      });

    const handleSelect = useCallback(
      async (index: number) => {
        if (!currentLootbox) return;

        const { data: prizeItem } = await playerLootboxActivation(
          currentLootbox.id
        );

        if (prizeItem) {
          const indexOfPrize = currentLootbox.items.findIndex(
            (lootbox) => lootbox.id === prizeItem.id
          );

          setCurrentLootbox({
            ...currentLootbox,
            items: update(
              index,
              currentLootbox.items[indexOfPrize],
              update(
                indexOfPrize,
                currentLootbox.items[index],
                currentLootbox.items
              )
            ),
          });

          setSelectedPrizeId(prizeItem.id);
          setIsStopedUpdating(true);
          fetchLootboxes();
        }
      },
      [currentLootbox, fetchLootboxes]
    );

    useEffect(() => {
      (async () => {
        try {
          setLootboxModalDataPending(true);

          const { data } = await fetchLootboxModal(language);

          setLootboxModalData(data);
        } catch (e) {
          console.log(e);
        } finally {
          setLootboxModalDataPending(false);
        }
      })();
    }, [language]);

    useEffect(() => {
      if (!withoutTagsLootboxes.length || lootboxId == null || isStopedUpdating)
        return;

      const lootbox = withoutTagsLootboxes.find(
        (lootbox) => lootbox.id === lootboxId
      );
      lootbox && setCurrentLootbox(lootbox);
    }, [isStopedUpdating, lootboxId, withoutTagsLootboxes]);

    if (lootboxModalDataPending) return <Skeleton />;

    return lootboxModalData ? (
      <Modal>
        <ScLootboxModal
          bgImage={
            lootboxModalData.background?.url ||
            getCDNImageUrl('/lootbox/defaultBg.webp')
          }
          ref={ref}
        >
          <ButtonClose onClick={onClose} {...buttonStyle} />

          <ImageComponent
            alt="character"
            className="character"
            height={208}
            src={lootboxModalData.character?.url || defaultChar}
            width={392}
          />

          <div className="info-block">
            {lootboxModalData && (
              <>
                <div className="title">{lootboxModalData.title}</div>
                <div
                  className="text"
                  dangerouslySetInnerHTML={{ __html: lootboxModalData.text }}
                />
              </>
            )}
            <TimerLabel timeLeft={timeLeft} />
            <div className="lootboxes-container">
              {currentLootbox &&
                currentLootbox.items?.map((lootbox, index) => (
                  <Lootbox
                    handleSelect={handleSelect}
                    index={index}
                    isChoiceMade={selectedPrizeId !== null && isStopedUpdating}
                    isSelected={selectedPrizeId === lootbox.id}
                    key={lootbox.id}
                    lootbox={lootbox}
                    onClose={onClose}
                  />
                ))}
            </div>
          </div>
        </ScLootboxModal>
      </Modal>
    ) : null;
  }
);
